import type { ConnectedPosition } from '@angular/cdk/overlay';
import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  ViewChild,
  computed,
  forwardRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import moment from 'moment';

import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MatMomentDateModule,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';

import { BaseControlValueAccessorDirective } from '../directives/base-control-value-accessor.directive';

export const MY_FORMATS = {
  parse: {
    dateInput: 'YYYY-MM-DD',
  },
  display: {
    dateInput: 'YYYY-MM-DD',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

const CALENDAR_POSITIONS: ConnectedPosition[] = [
  {
    originX: 'start',
    originY: 'bottom',
    overlayX: 'start',
    overlayY: 'top',
    offsetY: 8,
  },
  {
    originX: 'start',
    originY: 'top',
    overlayX: 'start',
    overlayY: 'bottom',
    offsetY: -8,
  },
];

@Component({
  selector: 'lib-custom-date-picker-control',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatMomentDateModule,
    MatIconModule,
    OverlayModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomDatePickerControlComponent),
      multi: true,
    },
    { provide: MAT_DATE_LOCALE, useValue: 'en-short-weekdays' },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
  templateUrl: './custom-date-picker-control.component.html',
  styleUrls: ['./custom-date-picker-control.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomDatePickerControlComponent
  extends BaseControlValueAccessorDirective<string>
  implements AfterViewInit
{
  private readonly hostElement = inject(ElementRef<HTMLElement>);

  @ViewChild('inputElement')
  private readonly inputElement!: ElementRef<HTMLInputElement>;

  protected readonly clearDateLabel = 'Clear date';

  public readonly placeholder = input<string>('');
  public readonly minDate = input<moment.Moment | null>(null);
  public readonly maxDate = input<moment.Moment | null>(null);

  public readonly widthPx = input<number | null>(null);
  public readonly heightPx = input<number | null>(null);

  public readonly appearance = input<'default' | 'form'>('default');
  public readonly hasError = input<boolean>(false);

  public readonly openOnFocus = input<boolean>(true);
  public readonly autoOpen = input<boolean>(false);
  public readonly compact = input<boolean>(false);

  public readonly showCalendarIcon = input(false);

  public readonly dateSelected = output<string>();
  public readonly cleared = output<void>();

  public readonly isCalendarOpen = signal(false);

  public readonly inputValue = computed(() => this.value() ?? '');

  protected readonly calendarPositions = CALENDAR_POSITIONS;

  public readonly selectedDate = computed(() => {
    const currentValue = this.value();

    if (!currentValue) {
      return null;
    }

    const parsedDate = moment(currentValue, 'YYYY-MM-DD', true);

    return parsedDate.isValid() ? parsedDate : null;
  });

  public ngAfterViewInit(): void {
    if (!this.autoOpen()) {
      return;
    }

    queueMicrotask(() => {
      this.openCalendar();
    });
  }

  @HostListener('document:mousedown', ['$event'])
  public onDocumentMouseDown(event: MouseEvent): void {
    const target = event.target as Node;

    if (!this.hostElement.nativeElement.contains(target)) {
      this.closeCalendar();
    }
  }

  public openCalendar(): void {
    if (this.disabled()) {
      return;
    }

    this.isCalendarOpen.set(true);
  }

  public openCalendarFromIcon(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.disabled()) {
      return;
    }

    this.openCalendar();
    this.inputElement.nativeElement.focus();
  }

  public clearCalendarInput(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.disabled()) {
      return;
    }

    this.updateValue(null);
    this.markAsTouched();

    this.cleared.emit();
    this.closeCalendar();
  }

  public closeCalendar(): void {
    this.isCalendarOpen.set(false);
  }

  public onFocus(): void {
    if (!this.openOnFocus()) {
      return;
    }

    this.openCalendar();
  }

  public onInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;

    this.updateValue(value);
    this.formatDateOnInput();
  }

  public onDateSelected(momentDate: moment.Moment | null): void {
    if (!momentDate) {
      return;
    }

    const formattedDate = momentDate.format('YYYY-MM-DD');

    this.updateValue(formattedDate);
    this.markAsTouched();

    this.dateSelected.emit(formattedDate);
    this.closeCalendar();
  }

  public validateAndFormatInput(): void {
    this.markAsTouched();

    const currentValue = this.value();

    if (!currentValue) {
      return;
    }

    const yearOnlyRegex = /^\d{4}$/;
    const yearMonthOnlyRegex = /^\d{4}-\d{2}$/;

    if (
      yearOnlyRegex.test(currentValue) ||
      yearMonthOnlyRegex.test(currentValue)
    ) {
      this.updateValue('');
      return;
    }

    const formats = ['YYYY-MM-DD', 'YYYYMMDD'];
    const parsedDate = moment(currentValue, formats, true);

    if (parsedDate.isValid()) {
      this.updateValue(parsedDate.format('YYYY-MM-DD'));
      return;
    }

    this.updateValue('');
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.closeCalendar();
      return;
    }

    const allowedKeys = [
      'Backspace',
      'Tab',
      'Enter',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'Home',
      'End',
    ];

    if (allowedKeys.includes(event.key)) {
      return;
    }

    if (!/\d/.test(event.key)) {
      event.preventDefault();
      return;
    }

    const inputElement = this.inputElement.nativeElement;

    const currentValue = inputElement.value;
    const selectionStart = inputElement.selectionStart;
    const selectionEnd = inputElement.selectionEnd;

    if (currentValue.length >= 10 && selectionStart === selectionEnd) {
      event.preventDefault();
    }
  }

  public clearInput(event: Event): void {
    event.stopPropagation();

    if (this.disabled()) {
      return;
    }

    this.updateValue(null);
    this.markAsTouched();

    this.cleared.emit();
    this.closeCalendar();

    queueMicrotask(() => {
      this.inputElement.nativeElement.focus();
    });
  }

  public focus(): void {
    this.inputElement.nativeElement.focus();

    if (this.autoOpen()) {
      this.openCalendar();
    }
  }

  private formatDateOnInput(): void {
    const currentValue = this.value();

    if (!currentValue) {
      return;
    }

    const originalValue = currentValue;

    let formattedValue = originalValue.replace(/\D/g, '').substring(0, 8);

    if (formattedValue.length > 4) {
      formattedValue = `${formattedValue.substring(
        0,
        4
      )}-${formattedValue.substring(4)}`;
    }

    if (formattedValue.length > 7) {
      formattedValue = `${formattedValue.substring(
        0,
        7
      )}-${formattedValue.substring(7)}`;
    }

    if (originalValue !== formattedValue) {
      this.updateValue(formattedValue);
    }
  }
}
